import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { Users, Copy, RefreshCw, Save, Download, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Household() {
  const { user } = useAuth();
  const [hh, setHh] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/household/me");
    setHh(data); setName(data.name || "");
  }, []);
  useEffect(() => { load(); }, [load]);

  const rename = async () => {
    try { await api.put("/household/me", { name }); toast.success("Household renamed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const regen = async () => {
    if (!confirm("Generate a new invite code? The old one will stop working.")) return;
    try { await api.post("/household/regenerate-code"); toast.success("New invite code"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const join = async () => {
    if (!code.trim()) return;
    try { await api.post("/household/join", { invite_code: code.trim() }); toast.success("Joined household"); setCode(""); load(); window.location.reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = () => {
    if (hh?.invite_code) {
      navigator.clipboard.writeText(hh.invite_code);
      toast.success("Invite code copied");
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/household/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `hearthouse-${(hh?.name || "household").toLowerCase().replace(/\s+/g, "-")}-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — a copy of everything.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const deleteHousehold = async () => {
    if (!deleteConfirm.trim()) return;
    setDeleting(true);
    try {
      await api.delete("/household/me", { data: { confirmation: deleteConfirm } });
      toast.success("Household deleted.");
      // Auth cookies were cleared server-side — force a full reload to hit /login.
      setTimeout(() => { window.location.href = "/login"; }, 600);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  const isOwner = user?.role === "owner";

  if (!hh) return <div className="text-hearth-mute font-display italic text-xl">Loading…</div>;

  return (
    <div>
      <PageHeader eyebrow="Family roster" title="Your household" subtitle="Share the invite code with people you live with. Everyone sees the same lists." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 warm-card p-8 space-y-6">
          <div>
            <Label className="caption text-hearth-mute">Household name</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="household-name-input" className="input-warm h-12" value={name} onChange={(e) => setName(e.target.value)} />
              <Button data-testid="household-save-name" onClick={rename} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6">
                <Save size={15} className="mr-1.5" /> Save
              </Button>
            </div>
          </div>

          <div>
            <Label className="caption text-hearth-mute">Invite code</Label>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <div data-testid="household-invite-code" className="font-mono text-2xl tracking-[0.2em] bg-hearth-cream border border-hearth-line rounded-2xl px-5 py-3 text-hearth-ink">
                {hh.invite_code}
              </div>
              <Button onClick={copy} data-testid="household-copy-code" variant="ghost" className="rounded-full"><Copy size={15} className="mr-1.5" /> Copy</Button>
              <Button onClick={regen} data-testid="household-regen-code" variant="ghost" className="rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10"><RefreshCw size={15} className="mr-1.5" /> Regenerate</Button>
            </div>
            <p className="text-xs text-hearth-mute mt-2">Anyone with this code can join your household and see all shared lists.</p>
          </div>

          <div className="border-t border-hearth-line pt-6">
            <Label className="caption text-hearth-mute">Join a different household</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="household-join-code" aria-label="Invite code to join a household" className="input-warm h-12 font-mono tracking-widest uppercase" placeholder="ABC12345" value={code} onChange={(e) => setCode(e.target.value)} />
              <Button data-testid="household-join-button" onClick={join} className="rounded-full bg-hearth-sage hover:bg-hearth-sageDark text-white px-6">Join</Button>
            </div>
          </div>
        </div>

        <div className="warm-card p-8">
          <div className="flex items-center gap-2 caption text-hearth-mute"><Users size={14} /> Members</div>
          <div className="mt-4 space-y-3">
            {(hh.members || []).map((m) => (
              <div key={m.id} className="flex items-center gap-3" data-testid={`household-member-${m.id}`}>
                <div className="w-10 h-10 rounded-full bg-hearth-sage text-white grid place-items-center font-medium">
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-hearth-ink font-medium truncate">{m.name}</div>
                  <div className="text-xs text-hearth-mute truncate">{m.email}</div>
                </div>
                <span className="caption text-hearth-mute">{m.role}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data & Privacy — export/delete (owner-only) + link to policy */}
      <section
        data-testid="household-data-privacy"
        className="warm-card p-8 mt-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-hearth-clay/10 text-hearth-clayDark grid place-items-center shrink-0">
            <ShieldCheck size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl text-hearth-ink">Your data, your rules</h3>
            <p className="text-hearth-mute mt-1 leading-relaxed">
              HearthHouse never sells your household data. Read the plain-language{" "}
              <Link to="/privacy" data-testid="household-privacy-link" className="text-hearth-clay underline">Privacy Policy</Link>{" "}
              for details on what we hold and why.
            </p>

            {isOwner ? (
              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <p className="caption text-hearth-mute">Export everything</p>
                  <p className="text-sm text-hearth-ink mt-1 mb-3">
                    A single JSON of every list, recipe, bill, plant, pet, and setting.
                  </p>
                  <Button
                    data-testid="household-export-btn"
                    onClick={exportData}
                    disabled={exporting}
                    variant="ghost"
                    className="rounded-full border border-hearth-line hover:bg-hearth-stone h-10"
                  >
                    {exporting
                      ? <Loader2 size={13} className="mr-1.5 animate-spin"/>
                      : <Download size={13} className="mr-1.5"/>}
                    Download data
                  </Button>
                </div>

                <div>
                  <p className="caption text-hearth-mute">Delete household forever</p>
                  <p className="text-sm text-hearth-ink mt-1 mb-3">
                    Removes every row + every member. Type <span className="italic">&quot;{hh.name}&quot;</span> below to confirm.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="household-delete-confirm"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder={hh.name}
                      autoComplete="off"
                      className="input-warm h-10 flex-1"
                    />
                    <Button
                      data-testid="household-delete-btn"
                      onClick={deleteHousehold}
                      disabled={
                        deleting ||
                        deleteConfirm.trim().toLowerCase() !== (hh.name || "").trim().toLowerCase()
                      }
                      variant="ghost"
                      className="rounded-full border border-hearth-coral/50 text-hearth-coral hover:bg-hearth-coral/10 h-10 shrink-0"
                    >
                      {deleting
                        ? <Loader2 size={13} className="mr-1.5 animate-spin"/>
                        : <Trash2 size={13} className="mr-1.5"/>}
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm italic text-hearth-mute mt-3">
                Only the household owner can export or delete. Ask them if you&apos;d like a copy.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
